import type { ChangeEvent } from 'react';
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import {
  CheckCircleFilled,
  ExclamationCircleFilled,
  LoadingOutlined,
} from '@ant-design/icons';
import { SignedFooterContent, type SignatureInfo } from './SignedFooterContent';
import { PreviewReportLink } from './PreviewReportLink';
import styles from './ReportFooter.module.css';

const isMac =
  typeof navigator !== 'undefined' &&
  /mac|iphone|ipad/i.test(navigator.userAgent);
const modKey = isMac ? '⌘' : 'Ctrl';

type ReportStatus = 'DRAFT' | 'PRELIMINARY' | 'FINAL';

interface ReportFooterProps {
  // Unsigned state props
  status: ReportStatus;
  onStatusChange: (status: ReportStatus) => void;
  /** Resolves `false` when the save failed; used to gate preview navigation. */
  onSave: () => void | Promise<boolean>;
  onFinalize: () => void;
  previewHref?: string;
  disabled?: boolean;
  isSaving?: boolean;
  unsavedChanges?: boolean;
  isFinalizing?: boolean;
  /** Role-gated. When false (ADMIN), hides Save, status dropdown, and Ctrl+S. */
  canEdit?: boolean;
  /**
   * Role-gated. When false (ADMIN, TECHNICIAN), hides Sign & Finalize,
   * Ctrl+Shift+S, and Add Addendum (signed footer).
   */
  canFinalize?: boolean;
  // Signed state props
  isSigned?: boolean;
  signature?: SignatureInfo;
  onAddAddendum?: () => void;
  isAddingAddendum?: boolean;
  onViewHistory?: () => void;
}

const STATUS_OPTIONS: { value: ReportStatus; label: string }[] = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'PRELIMINARY', label: 'Preliminary' },
];

// Sub-component for unsigned report footer
function UnsignedFooterContent({
  status,
  onStatusChange,
  onSave,
  onFinalize,
  previewHref,
  disabled,
  isSaving,
  unsavedChanges,
  isFinalizing,
  canEdit,
  canFinalize,
}: {
  status: ReportStatus;
  onStatusChange: (e: ChangeEvent<HTMLSelectElement>) => void;
  onSave: () => void | Promise<boolean>;
  onFinalize: () => void;
  previewHref?: string;
  disabled: boolean;
  isSaving: boolean;
  unsavedChanges: boolean;
  isFinalizing: boolean;
  canEdit: boolean;
  canFinalize: boolean;
}) {
  let saveStatusIcon = (
    <CheckCircleFilled
      className={`${styles.btnSaveIcon} ${styles.btnSaveIconSaved}`}
      aria-label="Saved"
    />
  );
  if (isSaving) {
    saveStatusIcon = (
      <LoadingOutlined
        spin
        className={styles.btnSaveIcon}
        aria-label="Saving"
      />
    );
  } else if (unsavedChanges) {
    saveStatusIcon = (
      <ExclamationCircleFilled
        className={`${styles.btnSaveIcon} ${styles.btnSaveIconUnsaved}`}
        aria-label="Unsaved changes"
      />
    );
  }
  return (
    <>
      {previewHref && (
        <PreviewReportLink href={previewHref} disabled={disabled} />
      )}

      {canEdit && (
        <div className={styles.status}>
          <label htmlFor="report-status" className={styles.statusLabel}>
            Status
          </label>
          <div className={styles.selectWrapper}>
            <select
              id="report-status"
              className={styles.select}
              value={status}
              onChange={onStatusChange}
              disabled={disabled}
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {canEdit && (
        <button
          type="button"
          className={styles.btn}
          onClick={onSave}
          disabled={disabled || isSaving}
          aria-label="Save"
        >
          {saveStatusIcon}
          <span className={styles.btnText}>
            <span aria-hidden="true" className={styles.btnTextSizer}>
              Saving...
            </span>
            <span className={styles.btnTextVisible}>
              {isSaving ? 'Saving...' : 'Save'}
            </span>
          </span>
          <span className={styles.btnShortcut}>
            <kbd className={styles.kbd}>{modKey}</kbd>
            <kbd className={styles.kbd}>S</kbd>
          </span>
        </button>
      )}

      {canFinalize && (
        <button
          type="button"
          className={styles.btnPrimary}
          onClick={onFinalize}
          disabled={disabled || isFinalizing}
        >
          {isFinalizing ? 'Finalizing...' : 'Sign & Finalize'}
          <span className={styles.btnShortcut}>
            <kbd className={styles.kbdPrimary}>{modKey}</kbd>
            <kbd className={styles.kbdPrimary}>⇧</kbd>
            <kbd className={styles.kbdPrimary}>S</kbd>
          </span>
        </button>
      )}
    </>
  );
}

export default function ReportFooter({
  status,
  onStatusChange,
  onSave,
  onFinalize,
  previewHref,
  disabled = false,
  isSaving = false,
  unsavedChanges = false,
  isFinalizing = false,
  canEdit = true,
  canFinalize = true,
  isSigned = false,
  signature,
  onAddAddendum,
  isAddingAddendum = false,
  onViewHistory,
}: ReportFooterProps) {
  const handleStatusChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      onStatusChange(e.target.value as ReportStatus);
    },
    [onStatusChange]
  );

  // A click on Save blurs the focused field first, so its onBlur commits
  // before the click. The hotkey must do that itself: blur, let React flush
  // the commit, then save through a ref so the fresh closure runs.
  const onSaveRef = useRef(onSave);
  const onFinalizeRef = useRef(onFinalize);
  useLayoutEffect(() => {
    onSaveRef.current = onSave;
    onFinalizeRef.current = onFinalize;
  });
  const commitFocusedField = () => {
    flushSync(() => (document.activeElement as HTMLElement | null)?.blur());
  };

  // Global hotkeys: Ctrl+S = Save (canEdit), Ctrl+Shift+S = Sign & Finalize
  // (canFinalize). Each side gated independently so technicians keep Ctrl+S
  // but lose Ctrl+Shift+S, and admins lose both.
  useEffect(() => {
    if (isSigned || disabled) return undefined;
    if (!canEdit && !canFinalize) return undefined;

    const handleKeyDown = (e: KeyboardEvent) => {
      const mod = isMac ? e.metaKey : e.ctrlKey;
      if (!mod || e.key.toLowerCase() !== 's') return;

      const wantFinalize = e.shiftKey;
      if (wantFinalize && !canFinalize) return;
      if (!wantFinalize && !canEdit) return;

      e.preventDefault();
      commitFocusedField();
      if (wantFinalize && !isFinalizing) {
        onFinalizeRef.current();
      } else if (!wantFinalize && !isSaving) {
        onSaveRef.current();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [
    isSigned,
    disabled,
    isSaving,
    isFinalizing,
    onSave,
    onFinalize,
    canEdit,
    canFinalize,
  ]);

  return (
    <div className={styles.shell}>
      <div className={styles.footer}>
        {isSigned ? (
          <SignedFooterContent
            signature={signature}
            previewHref={previewHref}
            onAddAddendum={onAddAddendum}
            isAddingAddendum={isAddingAddendum}
            onViewHistory={onViewHistory}
            canFinalize={canFinalize}
          />
        ) : (
          <UnsignedFooterContent
            status={status}
            onStatusChange={handleStatusChange}
            onSave={onSave}
            onFinalize={onFinalize}
            previewHref={previewHref}
            disabled={disabled}
            isSaving={isSaving}
            unsavedChanges={unsavedChanges}
            isFinalizing={isFinalizing}
            canEdit={canEdit}
            canFinalize={canFinalize}
          />
        )}
      </div>
    </div>
  );
}
